/**
 * ALL IS WELL EXPERIENCE™ — Health Passport Service
 *
 * Fetches authenticated patient's own records from Supabase.
 * All queries are RLS-secured: users only see their own data.
 * PROTOTYPE: Demo records are clearly labelled.
 */

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PassportAppointmentRow {
  id: string;
  doctorName: string;
  department: string;
  centreName: string;
  appointmentDate: string;
  appointmentTime: string;
  status: string;
  isDemo: boolean;
}

export interface PassportPrescriptionRow {
  id: string;
  doctorName: string;
  department: string;
  centreName: string;
  prescriptionDate: string;
  medicinesCount: number;
  status: string;
  isDemo: boolean;
}

export interface PassportLabReportRow {
  id: string;
  testName: string;
  category: string;
  centreName: string;
  reportDate: string;
  status: string;
  secureReferenceId: string;
  isDemo: boolean;
}

export interface PassportMedicineRow {
  id: string;
  medicineName: string;
  dosage: string;
  frequency: string;
  prescribedBy: string;
  startDate: string;
  endDate: string;
  status: string;
  isDemo: boolean;
}

export interface PassportVisitRow {
  id: string;
  department: string;
  centreName: string;
  visitDate: string;
  doctorName: string;
  visitType: string;
  isDemo: boolean;
}

export interface PassportMembershipRow {
  id: string;
  planName: string;
  status: string;
  validFrom: string;
  validUntil: string;
  memberId: string;
  isDemo: boolean;
}

// ─── Service Functions ────────────────────────────────────────────────────────

export async function fetchAppointments(): Promise<PassportAppointmentRow[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('appointments')
      .select('*')
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return data.map((r) => ({
      id: r.id,
      doctorName: r.doctor_name,
      department: r.department,
      centreName: r.centre_name,
      appointmentDate: r.appointment_date,
      appointmentTime: r.appointment_time,
      status: r.status,
      isDemo: r.is_demo,
    }));
  } catch { return []; }
}

export async function fetchPrescriptions(): Promise<PassportPrescriptionRow[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('prescriptions')
      .select('*')
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return data.map((r) => ({
      id: r.id,
      doctorName: r.doctor_name,
      department: r.department,
      centreName: r.centre_name,
      prescriptionDate: r.prescription_date,
      medicinesCount: r.medicines_count,
      status: r.status,
      isDemo: r.is_demo,
    }));
  } catch { return []; }
}

export async function fetchLabReports(): Promise<PassportLabReportRow[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('lab_reports')
      .select('*')
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return data.map((r) => ({
      id: r.id,
      testName: r.test_name,
      category: r.category,
      centreName: r.centre_name,
      reportDate: r.report_date,
      status: r.status,
      secureReferenceId: r.secure_reference_id,
      isDemo: r.is_demo,
    }));
  } catch { return []; }
}

export async function fetchMedicines(): Promise<PassportMedicineRow[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('medicines')
      .select('*')
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return data.map((r) => ({
      id: r.id,
      medicineName: r.medicine_name,
      dosage: r.dosage,
      frequency: r.frequency,
      prescribedBy: r.prescribed_by,
      startDate: r.start_date,
      endDate: r.end_date,
      status: r.status,
      isDemo: r.is_demo,
    }));
  } catch { return []; }
}

export async function fetchVisits(): Promise<PassportVisitRow[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('visits')
      .select('*')
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return data.map((r) => ({
      id: r.id,
      department: r.department,
      centreName: r.centre_name,
      visitDate: r.visit_date,
      doctorName: r.doctor_name,
      visitType: r.visit_type,
      isDemo: r.is_demo,
    }));
  } catch { return []; }
}

export async function fetchMembership(): Promise<PassportMembershipRow | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('memberships')
      .select('*')
      .eq('status', 'active')
      .maybeSingle();
    if (error || !data) return null;
    return {
      id: data.id,
      planName: data.plan_name,
      status: data.status,
      validFrom: data.valid_from,
      validUntil: data.valid_until,
      memberId: data.member_id,
      isDemo: data.is_demo,
    };
  } catch { return null; }
}
